/**
 * useIsMobile Composable
 * Reactive mobile/desktop detection using matchMedia API
 * Better than window.innerWidth for SSR compatibility and resize handling
 */

import { ref, onMounted, onUnmounted } from 'vue'

/**
 * Returns a reactive boolean indicating if the viewport matches the mobile query
 * @param query - CSS media query string (default: '(max-width: 767px)')
 * @returns Object with isMobile ref
 */
export function useIsMobile(query = '(max-width: 767px)') {
  // Default to false for SSR, will update on mount
  const isMobile = ref(false)

  let mediaQuery: MediaQueryList | null = null

  const updateMatch = (e: MediaQueryListEvent | MediaQueryList) => {
    isMobile.value = e.matches
  }

  onMounted(() => {
    // Check if window is available (client-side)
    if (typeof window !== 'undefined') {
      mediaQuery = window.matchMedia(query)
      // Set initial value
      isMobile.value = mediaQuery.matches
      // Listen for changes
      mediaQuery.addEventListener('change', updateMatch)
    }
  })

  onUnmounted(() => {
    if (mediaQuery) {
      mediaQuery.removeEventListener('change', updateMatch)
    }
  })

  return { isMobile }
}

/**
 * Breakpoint-specific composables for convenience
 */
export function useIsTablet() {
  return useIsMobile('(min-width: 768px) and (max-width: 1023px)')
}

export function useIsDesktop() {
  return useIsMobile('(min-width: 1024px)')
}

/**
 * Combined breakpoint detection
 * Returns current breakpoint as a string: 'mobile' | 'tablet' | 'desktop'
 */
export function useBreakpoint() {
  const { isMobile } = useIsMobile()
  const { isMobile: isTabletMatch } = useIsTablet()

  const breakpoint = ref<'mobile' | 'tablet' | 'desktop'>('desktop')

  onMounted(() => {
    if (typeof window !== 'undefined') {
      const mobileQuery = window.matchMedia('(max-width: 767px)')
      const tabletQuery = window.matchMedia('(min-width: 768px) and (max-width: 1023px)')

      const updateBreakpoint = () => {
        if (mobileQuery.matches) {
          breakpoint.value = 'mobile'
        } else if (tabletQuery.matches) {
          breakpoint.value = 'tablet'
        } else {
          breakpoint.value = 'desktop'
        }
      }

      updateBreakpoint()

      mobileQuery.addEventListener('change', updateBreakpoint)
      tabletQuery.addEventListener('change', updateBreakpoint)
    }
  })

  return { breakpoint, isMobile, isTablet: isTabletMatch }
}
